import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DollarSign, Clock, UtensilsCrossed, AlertCircle, Pencil, FolderTree, ListOrdered, Settings2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import PartnerLayout from "./PartnerLayout";
import MenuItemEditModal from "@/components/partner/MenuItemEditModal";

interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  image?: string;         // Thumbnail (300px) for lists
  headerImage?: string;   // Header (800px) for detail modal
  available: boolean;
  menuId: number;
  categoryId: number;
}

interface MenuCategory {
  id: number;
  name: string;
  description?: string;
  menuId: number;
  items: MenuItem[];
}

interface MenuData {
  categories: MenuCategory[];
  itemMenuMap: Record<string, number>;
}

export default function PartnerMenu() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const restaurantProfile = localStorage.getItem('partnerRestaurantProfile');
  const restaurantName = localStorage.getItem('partnerRestaurantName');
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const eatzyToken = sessionStorage.getItem('partnerEatzyToken');
  
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Check for valid token on mount - redirect to login if missing
  useEffect(() => {
    if (!eatzyToken) {
      toast({
        title: "Session Required",
        description: "Please log in to access the Partner Portal.",
        variant: "destructive",
      });
      navigate('/partner/login');
    }
  }, [eatzyToken, navigate, toast]);
  
  const { data: menuData, isLoading, isError, error } = useQuery<MenuData>({
    queryKey: ['/api/partner/menu', restaurantProfile],
    queryFn: async () => {
      if (!restaurantProfile) {
        throw new Error('No restaurant selected');
      }
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(`/api/partner/menu/${restaurantProfile}`, {
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
          'X-Eatzy-Token': eatzyToken || '',
          'X-Restaurant-Id': restaurantId || '',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch menu data');
      }
      return response.json();
    },
    enabled: !!restaurantProfile,
  });

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };
  
  const totalItems = menuData?.categories?.reduce((acc, cat) => acc + cat.items.length, 0) || 0;
  const availableItems = menuData?.categories?.reduce(
    (acc, cat) => acc + cat.items.filter(item => item.available).length, 0
  ) || 0;

  const handleEditItem = (item: MenuItem) => {
    setEditingItem(item);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingItem(null);
  };

  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-menu-title">Menu Management</h1>
            <p className="text-muted-foreground">
              Manage your restaurant's menu items, categories, and settings
            </p>
          </div>
          {menuData && (
            <div className="text-right">
              <p className="text-2xl font-bold">{totalItems}</p>
              <p className="text-xs text-muted-foreground">
                {availableItems} available items
              </p>
            </div>
          )}
        </div>
        
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Categories</CardTitle>
              <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold" data-testid="text-categories-count">
                  {menuData?.categories?.length || 0}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Items</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold" data-testid="text-items-count">
                  {totalItems}
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Available</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <div className="text-2xl font-bold text-green-600" data-testid="text-available-count">
                  {availableItems}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Menu Management Tabs */}
        <Tabs defaultValue="items" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="items" className="gap-2">
              <UtensilsCrossed className="h-4 w-4" />
              <span className="hidden sm:inline">Items</span>
            </TabsTrigger>
            <TabsTrigger value="categories" className="gap-2">
              <FolderTree className="h-4 w-4" />
              <span className="hidden sm:inline">Categories</span>
            </TabsTrigger>
            <TabsTrigger value="modifiers" className="gap-2">
              <ListOrdered className="h-4 w-4" />
              <span className="hidden sm:inline">Modifiers</span>
            </TabsTrigger>
            <TabsTrigger value="periods" className="gap-2">
              <Settings2 className="h-4 w-4" />
              <span className="hidden sm:inline">Periods</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Items Tab - Active Implementation */}
          <TabsContent value="items">
            <Card>
              <CardHeader>
                <CardTitle>Menu Items</CardTitle>
                <CardDescription>
                  {restaurantName ? `Menu for ${restaurantName}` : 'Restaurant menu items'} - Click the edit button to modify items
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : isError ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-2" />
                    <p className="text-sm text-destructive font-medium">Failed to load menu</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {error instanceof Error ? error.message : 'Please try again later'}
                    </p>
                  </div>
                ) : !restaurantProfile ? (
                  <div className="text-center py-8">
                    <UtensilsCrossed className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No restaurant selected. Please log in from the Partner Portal.
                    </p>
                  </div>
                ) : menuData?.categories && menuData.categories.length > 0 ? (
                  <Accordion type="multiple" className="w-full">
                    {menuData.categories.map((category) => (
                      <AccordionItem key={category.id} value={`category-${category.id}`}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <span className="font-semibold">{category.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {category.items.length} items
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {category.description && (
                            <p className="text-sm text-muted-foreground mb-4">{category.description}</p>
                          )}
                          <div className="space-y-3">
                            {category.items.map((item) => (
                              <div 
                                key={item.id} 
                                className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 group"
                              >
                                {item.image ? (
                                  <img 
                                    src={item.image} 
                                    alt={item.name}
                                    className="w-16 h-16 rounded-md object-cover shrink-0"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-md bg-muted flex items-center justify-center shrink-0">
                                    <UtensilsCrossed className="w-6 h-6 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{item.name}</p>
                                    {!item.available && (
                                      <Badge variant="outline" className="text-xs text-orange-600">
                                        Unavailable
                                      </Badge>
                                    )}
                                  </div>
                                  {item.description && (
                                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <p className="font-semibold">{formatPrice(item.price)}</p>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleEditItem(item)}
                                    data-testid={`btn-edit-item-${item.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : (
                  <div className="text-center py-8">
                    <UtensilsCrossed className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No menu items found. Add items to your menu in Eatzy to see them here.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Categories Tab - Future Implementation */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Menu Categories</CardTitle>
                <CardDescription>
                  Organize your menu items into categories
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <FolderTree className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">Category Management Coming Soon</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    You'll be able to create, edit, reorder, and delete menu categories. 
                    Drag and drop items between categories for easy organization.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Modifiers Tab - Future Implementation */}
          <TabsContent value="modifiers">
            <Card>
              <CardHeader>
                <CardTitle>Modifier Groups</CardTitle>
                <CardDescription>
                  Manage item customization options and add-ons
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <ListOrdered className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">Modifier Management Coming Soon</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Create modifier groups (e.g., "Size", "Toppings", "Extras") and assign them to menu items. 
                    Set required vs optional selections and pricing.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Menu Periods Tab - Future Implementation */}
          <TabsContent value="periods">
            <Card>
              <CardHeader>
                <CardTitle>Menu Periods</CardTitle>
                <CardDescription>
                  Set different menus for different times of day
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Settings2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold mb-2">Menu Period Management Coming Soon</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    Create time-based menus like Breakfast, Lunch, Dinner, and Happy Hour. 
                    Schedule when each menu is available and which items are included.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Edit Item Modal */}
      <MenuItemEditModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        item={editingItem}
        restaurantId={restaurantId || ''}
        restaurantProfile={restaurantProfile || ''}
      />
    </PartnerLayout>
  );
}
